import 'react-datasheet-grid/dist/style.css'; // Needs to be imported once only
import './datasheet/datasheet.css'; // After the grid's own styles, so its :root variables are overridden
import { api } from '@packages/backend/convex/_generated/api';
import { INTEGRATION_LABELS } from '@packages/shared';
import { createFileRoute, Link, Navigate, Outlet, useChildMatches } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { InfoIcon } from 'lucide-react';
import { ErrorAlert } from '@/components/ErrorAlert';
import { LoadingIcon } from '@/components/LoadingIcon';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { SuperAdminOnly } from '@/features/auth/SuperAdminOnly';
import { MenuTools } from './MenuTools';

export const Route = createFileRoute('/r/$restaurantId/menu')({
  // The menu pads its own tabs and grids.
  staticData: { noPagePadding: true },
  component: MenuLayout,
});

function MenuLayout() {
  const { restaurantId } = Route.useParams();
  // The menu page on screen, `undefined` on `/menu` itself. Not `useLocation()`: that moves to the
  // next URL while this layout is still mounted, so leaving the menu would redirect straight back.
  const menuPagePath = useChildMatches({ select: (matches) => matches[0]?.fullPath });
  const menuSourceIntegrationQuery = useQuery(api.integrations.getMenuSourceIntegration, {
    restaurantId,
  });

  if (menuSourceIntegrationQuery.isPending) return <LoadingIcon />;
  if (menuSourceIntegrationQuery.isError) {
    return (
      <ErrorAlert title="Error loading menu" message={menuSourceIntegrationQuery.error.message} />
    );
  }
  if (!menuPagePath) {
    return <Navigate params={{ restaurantId }} replace to="/r/$restaurantId/menu/menu-items" />;
  }
  // The integration a venue's menu comes from owns it, and every sync overwrites it: staff see the
  // menu read-only (`useIsMenuEditable`) under a notice. The menu mutations refuse too
  // (`menus/domain.assertMenuEditableX`).
  const menuSourceIntegration = menuSourceIntegrationQuery.data;

  return (
    <div className="p-2 md:p-6">
      <div className="space-y-4 mb-4">
        <div className={'flex'}>
          <h2 className="text-2xl font-bold tracking-tight">Menu</h2>
          <SuperAdminOnly>
            <MenuTools />
          </SuperAdminOnly>
        </div>

        {menuSourceIntegration && (
          <Alert>
            <InfoIcon />
            <AlertTitle>
              This menu comes from {INTEGRATION_LABELS[menuSourceIntegration]}.
            </AlertTitle>
            <AlertDescription>
              Edit it in {INTEGRATION_LABELS[menuSourceIntegration]}.
            </AlertDescription>
          </Alert>
        )}

        <div className={'flex justify-between'}>
          <div>
            <div className="flex h-5 items-center space-x-4 text-sm">
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary/70',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/menu-items'}
              >
                Menu Items
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/modifiers'}
              >
                Modifiers
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/categories'}
              >
                Categories
              </Link>
              <Separator orientation="vertical" />
              <Link
                activeProps={{
                  className: 'text-primary hover:text-primary',
                }}
                className={'font-semibold text-muted-foreground hover:text-primary/70'}
                params={{ restaurantId }}
                to={'/r/$restaurantId/menu/overview'}
              >
                Overview
              </Link>
            </div>
          </div>
        </div>
        <Separator className="my-0" />
      </div>
      <Outlet />
    </div>
  );
}
